import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { PANEL, ROW_HOVER } from '@/components/layouts/menu-styles';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** A labelled group of cards on a settings page (Figma "Settings V2"). */
export function SettingsSection({ label, children, className }: { label?: string; children: ReactNode; className?: string }) {
	return (
		<section className={cn('flex w-[600px] flex-col gap-3', className)}>
			{label && <p className='text-[12px] font-medium leading-4 text-gray-700'>{label}</p>}
			{children}
		</section>
	);
}

export function SettingsCard({ children, className }: { children: ReactNode; className?: string }) {
	return <div className={cn('flex w-full flex-col gap-4 rounded-2xl border border-gray-500 bg-white p-4', className)}>{children}</div>;
}

/** Dashed line between rows inside a card. */
export function SettingsDivider() {
	return <div className='w-full border-t border-dashed border-gray-500' />;
}

// Figma's dark glass tooltip: 11/16 white text, at most 280px wide.
export const SETTINGS_TOOLTIP_CLASS = 'max-w-[280px] text-[11px] leading-4 text-pretty shadow-sm';

/** The small (i) next to a title or column header. */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type='button'
					aria-label={label}
					className='flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-900 hover:bg-gray-100'
				>
					<Info className='size-3.5' />
				</button>
			</TooltipTrigger>
			<TooltipContent variant='dark' className={SETTINGS_TOOLTIP_CLASS}>
				{children}
			</TooltipContent>
		</Tooltip>
	);
}

interface ToggleRowProps {
	title: string;
	description?: ReactNode;
	/** Omit for a title-only row (a card heading). */
	checked?: boolean;
	onCheckedChange?: (next: boolean) => void;
	disabled?: boolean;
	/** Anything to put in place of, or beside, the switch. */
	action?: ReactNode;
}

/** Title + description with an optional trailing switch (Figma "Settings / Toggle Row"). */
export function ToggleRow({ title, description, checked, onCheckedChange, disabled, action }: ToggleRowProps) {
	const titleId = useId();
	return (
		<div className='flex w-full items-center gap-4'>
			<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
				<p id={titleId} className='text-[14px] font-medium leading-5 text-ink'>
					{title}
				</p>
				{description && <div className='text-[12px] leading-4 text-gray-900'>{description}</div>}
			</div>
			{action}
			{onCheckedChange && (
				<Switch aria-labelledby={titleId} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
			)}
		</div>
	);
}

/** A labelled input slot: label above, hint below. */
export function Field({ label, htmlFor, hint, error, children }: { label: string; htmlFor?: string; hint?: ReactNode; error?: string; children: ReactNode }) {
	return (
		<div className='flex w-full flex-col gap-1'>
			<div className='flex w-full flex-col gap-2'>
				<label htmlFor={htmlFor} className='text-[12px] font-medium leading-4 text-gray-900'>
					{label}
				</label>
				{children}
			</div>
			{error ? (
				<p className='text-[11px] leading-4 text-red-500'>{error}</p>
			) : (
				hint && <div className='text-[11px] leading-4 text-gray-800'>{hint}</div>
			)}
		</div>
	);
}

/** Shared look of a text/number/select box on these pages. */
export const SETTINGS_INPUT_CLASS =
	'h-10 w-full rounded-[12px] border border-gray-500 bg-white px-3 text-[13px] text-ink outline-none placeholder:text-[12px] placeholder:text-gray-800 focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-60';

export const SETTINGS_TEXTAREA_CLASS =
	'min-h-20 w-full resize-y rounded-2xl border border-gray-500 bg-white px-3 py-2.5 text-[13px] leading-[18px] text-ink outline-none placeholder:text-gray-800 focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-60';

/** A Radix Select trigger and menu in the settings input style. */
export const SETTINGS_SELECT_TRIGGER_CLASS = 'h-10 w-full rounded-[12px] border border-gray-500 bg-white px-3 text-[13px] text-ink';
// The account menu's panel and rows; long lists scroll natively (no Radix arrows), long names don't widen it.
export const SETTINGS_SELECT_CONTENT_CLASS = `${PANEL} max-h-72 max-w-[max(320px,var(--radix-select-trigger-width))] [&_[data-slot^=select-scroll]]:hidden`;
export const SETTINGS_SELECT_ITEM_CLASS = `${ROW_HOVER} cursor-pointer rounded-[10px] py-1 pl-2.5 pr-8 text-[13px] leading-[18px] text-ink`;

/** Cancel / Save Changes, as on every settings Save bar. */
export const SETTINGS_CANCEL_BUTTON_CLASS =
	'h-[30px] cursor-pointer rounded-[20px] border border-gray-300 bg-white px-3 text-[12px] font-medium text-ink transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50';
export const SETTINGS_SAVE_BUTTON_CLASS =
	'h-[30px] cursor-pointer rounded-full border border-text-quote bg-ink px-3.5 text-[12px] font-normal text-gray-50 hover:bg-ink-strong disabled:cursor-not-allowed disabled:opacity-50';

/** Header action button, beside the search box (Figma "Secondary Button", e.g. Filters). */
export const SETTINGS_HEADER_BUTTON_CLASS =
	'flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-full border border-gray-500 bg-white px-4 text-[13px] leading-[18px] text-ink-strong transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50';

/** Small pill button (Figma "Button / Add"). */
export const SETTINGS_PILL_BUTTON_CLASS =
	'flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-gray-500 bg-white px-3 text-[13px] leading-[18px] text-ink-strong transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50';

export function SettingsCardSkeleton({ rows = 2 }: { rows?: number }) {
	return (
		<SettingsCard>
			{Array.from({ length: rows }).map((_, i) => (
				<div key={i} className='flex items-center justify-between gap-4'>
					<div className='flex flex-1 flex-col gap-1.5'>
						<Skeleton className='h-4 w-48' />
						<Skeleton className='h-3 w-72' />
					</div>
					<Skeleton className='h-6 w-14 rounded-full' />
				</div>
			))}
		</SettingsCard>
	);
}

export function SettingsError({ children = 'Failed to load settings. Please try again later.' }: { children?: ReactNode }) {
	return <div className='w-[600px] rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600'>{children}</div>;
}

/** The amber "you can't edit this" strip shown to view-only roles. */
export function ReadOnlyNotice() {
	return (
		<p className='w-[600px] rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
			You have read-only access to these settings.
		</p>
	);
}

interface CommitNumberInputProps {
	value: number;
	min: number;
	max: number;
	step?: number;
	/** Round to whole numbers before clamping. */
	integer?: boolean;
	onCommit: (next: number) => void;
	disabled?: boolean;
	'aria-label': string;
	className?: string;
}

/** A number box that clamps and commits on blur, so "15" can be typed without "1" clamping first. */
export function CommitNumberInput({ value, min, max, step = 1, integer = true, onCommit, disabled, className, ...rest }: CommitNumberInputProps) {
	// Synced through a ref, not remounted, so a commit that moves a sibling bound keeps Tab's focus.
	const ref = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (ref.current) ref.current.value = String(value);
	}, [value]);
	return (
		<input
			ref={ref}
			type='number'
			inputMode={integer ? 'numeric' : 'decimal'}
			aria-label={rest['aria-label']}
			min={min}
			max={max}
			step={step}
			disabled={disabled}
			defaultValue={value}
			onBlur={(e) => {
				const raw = e.target.valueAsNumber;
				const rounded = integer ? Math.round(raw) : raw;
				const next = Number.isNaN(raw) ? value : Math.min(max, Math.max(min, rounded));
				e.target.value = String(next);
				if (next !== value) onCommit(next);
			}}
			className={cn(SETTINGS_INPUT_CLASS, 'w-[72px]', className)}
		/>
	);
}
